/**
 * Attachment Utility Functions
 */

/**
 * Normalize file URL to relative path
 * 
 * Converts absolute URLs (e.g., "https://site.com/files/image.png")
 * to relative paths (e.g., "/files/image.png")
 * 
 * This ensures consistency with Frappe's field storage pattern
 * where fields typically store relative paths.
 * 
 * @param fileUrl - Absolute or relative file URL
 * @returns Relative file path (e.g., "/files/image.png" or "/private/files/doc.pdf")
 */
export function normalizeFileUrl(fileUrl: string | null | undefined): string | null {
  if (!fileUrl) {
    return null;
  }
  
  // If already relative, return as-is
  if (fileUrl.startsWith("/")) {
    return fileUrl;
  }
  
  // If it's a data URL or external URL that doesn't match our pattern, return as-is
  if (fileUrl.startsWith("data:") || fileUrl.startsWith("http://") || fileUrl.startsWith("https://")) {
    // Extract relative path from absolute URL
    // Pattern: https://site.com/files/image.png -> /files/image.png
    // Pattern: https://site.com/private/files/doc.pdf -> /private/files/doc.pdf
    
    try {
      const url = new URL(fileUrl);
      const pathname = url.pathname;
      
      // If pathname starts with /files/ or /private/files/, return it
      if (pathname.startsWith("/files/") || pathname.startsWith("/private/files/")) {
        return pathname;
      }
      
      // Otherwise, return the full absolute URL (external file)
      return fileUrl;
    } catch {
      // Invalid URL, return as-is
      return fileUrl;
    }
  }
  
  // If it doesn't start with /, assume it's a filename and prepend /files/
  if (!fileUrl.includes("/")) {
    return `/files/${fileUrl}`;
  }
  
  // Return as-is if we can't determine
  return fileUrl;
}

/**
 * Format file size for display
 * 
 * @param bytes - File size in bytes
 * @returns Formatted string (e.g., "2.3 MB")
 */
export function formatFileSize(bytes: number | undefined | null): string {
  if (!bytes || bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
}
